"use client"

import camperParkeLogo from '@/components/gfx/wassersportzentrum-stellplatz-zingst.png'
import { captureClientEvent } from "@/lib/telemetry-client";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import Link from "next/link";
import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa6";
import Logo from "../ui/logo";
import Image from 'next/image';
import { motion, AnimatePresence } from "framer-motion";

export default function NavDesktop() {
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="fixed top-0 z-50 hidden w-full md:block"
        >
            <div className="mx-auto max-w-7xl px-4 pt-4">
                <motion.nav
                    animate={{
                        backgroundColor: isScrolled ? "rgba(30, 41, 59, 0.85)" : "rgba(30, 41, 59, 0)",
                        backdropFilter: isScrolled ? "blur(12px)" : "blur(0px)",
                    }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-between rounded-2xl px-4 py-3 transition-shadow duration-300"
                    style={{
                        boxShadow: isScrolled ? "0 8px 32px rgba(0, 0, 0, 0.12)" : "none",
                    }}
                >
                    {/* Logo section - stacked logos */}
                    <Link
                        href="/"
                        className="group flex items-center"
                        onClick={() => captureClientEvent("navigation_clicked", { section: "logo", target: "/" })}
                    >
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 400 }}
                            className={`relative flex items-center justify-center overflow-hidden rounded-full border-2 border-amber-400 bg-slate-800 transition-all duration-300 ${isScrolled ? 'h-16 w-16' : 'h-20 w-20'}`}
                        >
                            <Image
                                src={camperParkeLogo}
                                alt="Stellplatz Zingst Ostsee"
                                className="h-full w-full object-cover"
                            />
                        </motion.div>
                        <motion.div
                            whileHover={{ scale: 1.05, rotate: 3 }}
                            transition={{ type: "spring", stiffness: 400 }}
                            className={`relative -ml-6 flex items-center justify-center rounded-full border-2 border-amber-400 bg-slate-800 p-3 transition-all duration-300 ${isScrolled ? 'h-16 w-16' : 'h-20 w-20'}`}
                        >
                            <Logo className={`fill-amber-400 transition-all duration-300 ${isScrolled ? 'h-8 w-8' : 'h-10 w-10'}`} />
                        </motion.div>
                        <AnimatePresence>
                            {isScrolled && (
                                <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -10 }}
                                    className="ml-3 font-special text-lg font-bold text-white"
                                >
                                    <p className="font-special text-xl uppercase">Stellplatz Zingst</p>
                                    <p className="font-handwritten text-lg tracking-wider text-amber-300">Wohnmobilstellplatz Ostsee</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Link>

                    {/* Navigation items */}
                    <div className="flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="group flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white/90 transition-all duration-300 hover:bg-white/10 hover:text-white">
                                    Stellplatz
                                    <FaChevronDown className="h-3 w-3 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="center"
                                className="mt-2 w-48 rounded-xl border-0 bg-slate-800/95 p-2 shadow-xl backdrop-blur-lg"
                            >
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/#preise"
                                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                                        onClick={() => captureClientEvent("navigation_clicked", { section: "stellplatz_dropdown", target: "preise" })}
                                    >
                                        Preise & Bezahlung
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/#anfahrt"
                                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                                        onClick={() => captureClientEvent("navigation_clicked", { section: "stellplatz_dropdown", target: "anfahrt" })}
                                    >
                                        Anfahrt
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/#information"
                                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                                        onClick={() => captureClientEvent("navigation_clicked", { section: "stellplatz_dropdown", target: "information" })}
                                    >
                                        Informationen
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/#faq"
                                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                                        onClick={() => captureClientEvent("navigation_clicked", { section: "stellplatz_dropdown", target: "faq" })}
                                    >
                                        FAQ
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/#regeln"
                                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/90 transition-colors hover:bg-white/10 hover:text-white"
                                        onClick={() => captureClientEvent("navigation_clicked", { section: "stellplatz_dropdown", target: "regeln" })}
                                    >
                                        Regeln
                                    </Link>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <a
                            href="https://surfandwave.club"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => captureClientEvent("navigation_clicked", { section: "desktop_nav", target: "surfschule" })}
                        >
                            <button className="rounded-full px-4 py-2 text-sm font-medium text-white/90 transition-all duration-300 hover:bg-white/10 hover:text-white">
                                Surfschule
                            </button>
                        </a>

                        <Link href="/jobs" onClick={() => captureClientEvent("navigation_clicked", { section: "desktop_nav", target: "jobs" })}>
                            <button className="rounded-full px-4 py-2 text-sm font-medium text-white/90 transition-all duration-300 hover:bg-white/10 hover:text-white">
                                Jobs
                            </button>
                        </Link>

                        <Link href="/kontakt" onClick={() => captureClientEvent("navigation_clicked", { section: "desktop_nav", target: "kontakt" })}>
                            <button className="rounded-full px-4 py-2 text-sm font-medium text-white/90 transition-all duration-300 hover:bg-white/10 hover:text-white">
                                Kontakt
                            </button>
                        </Link>

                        <Link href="/#preise" onClick={() => captureClientEvent("navigation_clicked", { section: "desktop_nav", target: "ticket_kaufen" })}>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="ml-2 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-lg shadow-amber-500/20 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/30"
                            >
                                Ticket kaufen
                            </motion.button>
                        </Link>
                    </div>
                </motion.nav>
            </div>
        </motion.div>
    );
}
